/**
 * handlers/chat.ts  [MODIFIED — Enneagram Personality System Integration]
 * Core send/stream logic: handleSend, SSE onEvent, empathy helpers, /story command.
 *
 * LAZY-LOAD REFACTOR:
 *   Previously this module held static imports of `updateAvatarCognitiveState`,
 *   `updateAvatarEmotion`, and `applyPersonalityModifiers` from `../avatar-3d`,
 *   which was the single largest contributor to Three.js + Loom3 landing in the
 *   main synchronous bundle (924 kB).
 *
 *   Fix: the static imports are removed and replaced with module-level no-op
 *   stubs.  main.ts calls `registerAvatarCallbacks()` after the avatar chunk
 *   has loaded, wiring the real functions.  The stubs guarantee that rapid
 *   user messages sent before the avatar module resolves don't throw.
 */

import { state, STORAGE_KEY } from '../state';
import { dom } from '../dom';
import {
  chatStream, draftStream, prepStream,
} from '../api';
import {
  appendMessage, appendTypingIndicator, removeTypingIndicator,
  hideSuggestions, enhanceCodeBlocks, showToast,
  renderDocumentSuggestions, setToolCallStatus,
  renderThreadRecallBanner, renderTransparencyPopover,
  setWaveformContext,
} from '../ui-renderers';
import { handleActionMarkers, renderSourcesPanel, renderGroundedBadge } from '../actions';
import { renderMarkdown } from '../markdown';
import {
  startBrainwaveAnimation, stopBrainwaveAnimation,
  startSoundscape, stopSoundscape,
} from '../effects';
import { showWellnessNudge } from '../emotion-avatar';
import {
  TOOL_LABELS, TOOL_COG_STATE, FALLBACK_STORIES,
  _toolNameToCognitive, updateSendState, getUserName,
} from './helpers';
import { showChat } from './views';
import { handleMilestoneNotification } from './milestone';

// ── [PERSONALITY] Imports ────────────────────────────────────────────────────
import {
  loadOrCreateProfile,
  saveProfile,
  biasEmotion,
  evolvePersonality,
  getSystemPromptInsert,
  getAvatarModifiers,
  resolveUserId,
} from '../personality-engine';
import {
  computeClientSentiment,
  extendedSentimentToScore,
} from '../sentiment-lite';
import type { PersonalityProfile } from '../personality-types';
import type { AvatarModifiers } from '../personality-types';

// ── LAZY-LOAD BRIDGE — avatar-3d callbacks ────────────────────────────────────
//
// Populated by registerAvatarCallbacks() once main.ts has lazy-loaded ./avatar-3d.
// Until then every call is a silent no-op — safe because:
//   • The 3D canvas is not mounted before init() resolves.
//   • The avatar does not need to react to pre-mount events.

let _updateAvatarCognitiveState: (cogState: string) => void             = () => {};
let _updateAvatarEmotion:        (emotion: string, animate?: boolean) => void = () => {};
let _applyPersonalityModifiers:  (modifiers: AvatarModifiers) => void   = () => {};

/**
 * Called by main.ts after `import('./avatar-3d')` resolves.
 * Wires the real avatar functions so subsequent chat events drive the 3D canvas.
 *
 * All three functions are updated atomically so there is no window where
 * one is real and another is a no-op.
 */
export function registerAvatarCallbacks(cbs: {
  updateAvatarCognitiveState: (cogState: string) => void;
  updateAvatarEmotion:        (emotion: string, animate?: boolean) => void;
  applyPersonalityModifiers:  (modifiers: AvatarModifiers) => void;
}): void {
  _updateAvatarCognitiveState = cbs.updateAvatarCognitiveState;
  _updateAvatarEmotion        = cbs.updateAvatarEmotion;
  _applyPersonalityModifiers  = cbs.applyPersonalityModifiers;
}

// ── [PERSONALITY] Module-level profile state ─────────────────────────────────
let _personalityProfile: PersonalityProfile | null = null;
let _pendingSentimentScore: number | null = null;

function _getProfile(): PersonalityProfile {
  if (!_personalityProfile) {
    const userId = resolveUserId();
    _personalityProfile = loadOrCreateProfile(userId);
    _initAvatarPersonality(_personalityProfile);
  }
  return _personalityProfile;
}

function _initAvatarPersonality(profile: PersonalityProfile): void {
  const modifiers = getAvatarModifiers(profile);
  _applyPersonalityModifiers(modifiers);
}

// ── /story synthetic stream helper ────────────────────────────────────────────

async function _streamFallbackStory(): Promise<void> {
  _updateAvatarCognitiveState('recalling');
  appendTypingIndicator();

  await new Promise<void>(r => setTimeout(r, 600));
  removeTypingIndicator();

  const story = FALLBACK_STORIES[Math.floor(Math.random() * FALLBACK_STORIES.length)];
  const bubble = appendMessage('bot', '');

  const bodyEl = bubble?.querySelector('.ai-msg-body');
  if (!bodyEl) {
    _updateAvatarCognitiveState('idle');
    return;
  }

  let textEl = bodyEl.querySelector('.ai-msg-text') as HTMLElement | null;
  if (!textEl) {
    textEl = document.createElement('div');
    textEl.className = 'ai-msg-text';
    bodyEl.appendChild(textEl);
  }

  const words = story.split(' ');
  let accumulated = '';
  for (const word of words) {
    accumulated += (accumulated ? ' ' : '') + word;
    textEl.innerHTML = renderMarkdown(accumulated);
    const msgsEl = dom.msgs;
    if (msgsEl) msgsEl.scrollTop = msgsEl.scrollHeight;
    await new Promise<void>(r => setTimeout(r, 40));
  }

  textEl.innerHTML = renderMarkdown(accumulated);
  if (bubble) enhanceCodeBlocks(bubble);

  _updateAvatarCognitiveState('idle');
}

// ── Empathy helpers ───────────────────────────────────────────────────────────

export function splitEmpathyLine(text: string): { empathy: string; rest: string } {
  if (!text) return { empathy: '', rest: text };

  const EMPATHY_PATTERNS = [
    /^(I understand[^.!?—\-–]*[.!?])\s+/i,
    /^(I can see[^.!?—\-–]*[.!?])\s+/i,
    /^(That sounds[^.!?—\-–]*[.!?])\s+/i,
    /^(That must[^.!?—\-–]*[.!?])\s+/i,
    /^(I'm sorry[^.!?—\-–]*[.!?])\s+/i,
    /^(I know this[^.!?—\-–]*[.!?])\s+/i,
    /^(I appreciate[^.!?—\-–]*[.!?])\s+/i,
    /^(I hear you[.!?])\s+/i,
    /^(This sounds[^.!?—\-–]*[.!?])\s+/i,
    /^(That's difficult[^.!?—\-–]*[.!?])\s+/i,
  ];

  const SEPARATOR_PATTERN = /^([^—\-–\n]{10,80}(?:—|–|-{2})[^—\-–\n]{5,80}\.)\s+([\s\S]+)/;

  for (const pattern of EMPATHY_PATTERNS) {
    const m = text.match(pattern);
    if (m) return { empathy: m[1].trim(), rest: text.slice(m[0].length).trimStart() };
  }

  const m2 = text.match(SEPARATOR_PATTERN);
  if (m2) return { empathy: m2[1].trim(), rest: m2[2].trimStart() };

  return { empathy: '', rest: text };
}

function _updateBubbleEmpathy(bubble: HTMLElement, text: string) {
  if (!bubble || !text) return;
  const bodyEl = bubble.querySelector('.ai-msg-body');
  if (!bodyEl) return;
  let empLine = bodyEl.querySelector('.ai-empathy-line') as HTMLElement | null;
  if (!empLine) {
    empLine = document.createElement('div');
    empLine.className = 'ai-empathy-line';
    empLine.setAttribute('aria-label', 'Empathy note');
    bodyEl.insertBefore(empLine, bodyEl.firstChild);
  }
  empLine.textContent = text;
}

// ── Main send handler ─────────────────────────────────────────────────────────

export async function handleSend() {
  const input = dom.input;
  const text  = (input ? input.value : '').trim();
  if (!text || state.streaming) return;

  if (state.view === 'history') showChat();

  if (input) {
    input.value = '';
    input.style.height = 'auto';
  }
  updateSendState();
  hideSuggestions();
  appendMessage('user', text);
  if (window.navigator && window.navigator.vibrate) window.navigator.vibrate(20);

  // ── /soundscape command intercept ─────────────────────────────────────────
  const soundscapeMatch = text.match(/^\/(soundscape|sound|focus)(\s+(on|off|stop|start))?$/i);
  if (soundscapeMatch) {
    const subCmd = (soundscapeMatch[3] || '').toLowerCase();
    if (subCmd === 'off' || subCmd === 'stop') {
      stopSoundscape();
      appendMessage('bot', 'Soundscape paused. 🤫');
    } else {
      startSoundscape();
      appendMessage('bot', 'Ambient focus soundscape enabled. 🎵 Type /soundscape off to stop.');
    }
    return;
  }

  // ── /story command intercept ──────────────────────────────────────────────
  const storyMatch = text.match(/^\/(story|hadithi|simulizi)/i);
  if (storyMatch) {
    void _streamFallbackStory();
    return;
  }

  // ── [PERSONALITY] /personality debug command ────────────────────────────
  if (/^\/personality(\s|$)/i.test(text)) {
    const profile  = _getProfile();
    const mods     = getAvatarModifiers(profile);
    const typeDef  = (await import('../personality-engine')).ENNEAGRAM_TYPES[profile.primaryType];
    const wingStr  = profile.wing ? ` w${profile.wing}` : '';
    const summary  = [
      `**Kili's Personality** (debug)`,
      `Type: ${profile.primaryType} – ${typeDef.name}${wingStr}`,
      `Health: ${profile.healthLevel.toFixed(2)} / 9.0`,
      `Turns: ${profile.turnCount}`,
      `Hue: ${mods.absoluteHueDeg.toFixed(0)}°`,
      `Cognitive pace: ×${mods.cognitivePaceMult.toFixed(2)}`,
      `Resting AUs: ${JSON.stringify(mods.restingAUs)}`,
    ].join('\n');
    appendMessage('bot', summary);
    return;
  }

  // ── [PERSONALITY] Ensure profile is loaded ──────────────────────────────
  const profile = _getProfile();
  _pendingSentimentScore = null;

  state.streaming = true;

  const sb   = dom.sendBtn;
  const fbEl = dom.fab;
  if (sb)   { sb.classList.add('spin'); sb.disabled = true; }
  if (fbEl) fbEl.classList.add('streaming');

  appendTypingIndicator();
  _updateAvatarCognitiveState('thinking');

  let botBubble:     HTMLElement | null = null;
  let botText        = '';
  let firstDelta     = false;
  let backendEmpathy = '';
  let docSuggestions: any[] = [];

  try {
    // Build request body
    const mode    = state.draftMode ? 'draft' : (state.prepMode ? 'prep' : 'chat');
    const token   = (typeof AkiliPortal !== 'undefined' && AkiliPortal.getToken)
      ? AkiliPortal.getToken() : '';

    const bodyObj: Record<string, unknown> = {
      message:        text,
      conversationId: state.conversationId || null,
      section:        window._currentSection || null,
      matterId:       state.currentMatterId  || null,
      documentId:     state.attachedDocumentId || null,
    };

    if (state.draftMode) {
      bodyObj.docType   = state.draftDocType || 'letter';
    } else if (state.prepMode) {
      bodyObj.prepType  = state.prepType || 'argument';
    }

    // [PERSONALITY] Inject system prompt modifier
    const systemInsert = getSystemPromptInsert(profile);
    if (systemInsert) bodyObj.systemInsert = systemInsert;

    const onEvent = (evt: any) => {
      if (!evt || !evt.type) return;

      // ── Tool-call status ──────────────────────────────────────────────────
      if (evt.type === 'tool_call_start') {
        const toolName = evt.toolName || evt.tool || '';
        const label    = TOOL_LABELS[toolName] || evt.label || evt.tool || 'Checking…';
        state.toolCallStatus = label;
        setToolCallStatus(label);
        setWaveformContext(label);
        const cogState = TOOL_COG_STATE[toolName] || _toolNameToCognitive(toolName);
        _updateAvatarCognitiveState(cogState);
        return;
      }
      if (evt.type === 'tool_call_end') {
        state.toolCallStatus = null;
        setToolCallStatus(null);
        setWaveformContext('Thinking...');
        _updateAvatarCognitiveState('thinking');
        return;
      }

      // ── Milestone notification ────────────────────────────────────────────
      if (evt.type === 'milestone') {
        handleMilestoneNotification(evt);
        return;
      }

      // ── Standard event types ──────────────────────────────────────────────
      if (evt.type === 'meta') {
        if (evt.conversationId) {
          state.conversationId = evt.conversationId;
          sessionStorage.setItem(STORAGE_KEY, state.conversationId);
        }
        if (evt.empathy && typeof evt.empathy === 'string') {
          backendEmpathy = evt.empathy;
        }
        if (evt.sentiment !== undefined) {
          state.sentimentFromMeta = evt.sentiment;
          const waveformCanvas = document.querySelector('.ai-waveform-canvas') as HTMLCanvasElement | null;
          if (waveformCanvas) {
            stopBrainwaveAnimation();
            startBrainwaveAnimation(waveformCanvas, evt.sentiment);
          }
          _pendingSentimentScore = extendedSentimentToScore(evt.sentiment);
        }

        // [PERSONALITY] Bias avatar emotion through personality filter
        if (evt.emotion) {
          const biased = biasEmotion(evt.emotion, profile);
          _updateAvatarEmotion(biased.emotion);
          // [FIX] Dispatch for EmotionWheelVisualizer.injectMiniBadge() in main.ts
          document.dispatchEvent(new CustomEvent('akili:emotion-analysis', { detail: biased, bubbles: true }));
        }

        if (evt.cognitiveState)  _updateAvatarCognitiveState(evt.cognitiveState);
        if (evt.shouldNudge)     showWellnessNudge();
        if (evt.riskSignals && evt.riskSignals.length > 0) {
          import('../ui-renderers.js').then(m => m.renderRiskFingerprint(evt.riskSignals));
        }

      } else if (evt.type === 'sources') {
        if (!botBubble) botBubble = appendMessage('bot', '', undefined, { empathy: backendEmpathy });
        renderSourcesPanel(evt.sources, botBubble);
        if (evt.grounded) renderGroundedBadge(botBubble);

      } else if (evt.type === 'delta') {
        if (!botBubble) {
          removeTypingIndicator();
          botBubble = appendMessage('bot', '', undefined, { empathy: backendEmpathy });
        }
        if (!firstDelta) {
          firstDelta = true;
          _updateAvatarCognitiveState('idle');
        }
        if (evt.empathy && !backendEmpathy) {
          backendEmpathy = evt.empathy;
          _updateBubbleEmpathy(botBubble, backendEmpathy);
        }
        botText += evt.text;
        const cleanedText = handleActionMarkers(botText);
        botText = cleanedText;
        const bodyEl = botBubble.querySelector('.ai-msg-body');
        if (bodyEl) {
          let textContainer = bodyEl.querySelector('.ai-msg-text') as HTMLElement | null;
          if (!textContainer) {
            textContainer = document.createElement('div');
            textContainer.className = 'ai-msg-text';
            const badge = bodyEl.querySelector('.ai-grounded-badge');
            const existingEmpathy = bodyEl.querySelector('.ai-empathy-line');
            bodyEl.innerHTML = '';
            if (existingEmpathy) bodyEl.appendChild(existingEmpathy);
            if (badge) bodyEl.appendChild(badge);
            bodyEl.appendChild(textContainer);
          }
          textContainer.innerHTML = renderMarkdown(cleanedText);
        }
        const msgsEl = dom.msgs;
        if (msgsEl) msgsEl.scrollTop = msgsEl.scrollHeight;

      } else if (evt.type === 'done') {
        if (evt.documentSuggestions && Array.isArray(evt.documentSuggestions)) {
          docSuggestions = evt.documentSuggestions;
        }
        const stored = botText;
        if (state.convCache && state.conversationId) {
          const conv = state.convCache.find((c: any) => c.id === state.conversationId);
          if (conv) {
            conv.turn_count   = (conv.turn_count || 0) + 2;
            conv.last_message = stored.slice(0, 80);
          }
        }
        if (botBubble) {
          enhanceCodeBlocks(botBubble);
          if (docSuggestions && docSuggestions.length) {
            renderDocumentSuggestions(docSuggestions, botBubble);
          }
          if (evt.precedents && evt.precedents.length > 0) {
            import('../ui-renderers.js').then(m => m.renderPrecedentRail(evt.precedents, botBubble));
          }
        }
        if (evt.context_used && botBubble) {
          renderTransparencyPopover(evt.context_used, botBubble);
        }
        state.toolCallStatus = null;
        setToolCallStatus(null);
        setWaveformContext(null);
        _updateAvatarCognitiveState('idle');

        // ── [PERSONALITY] Evolve personality after each turn ────────────
        try {
          const sentimentScore = _pendingSentimentScore !== null
            ? _pendingSentimentScore
            : computeClientSentiment(text).score;

          const { profile: evolved, delta } = evolvePersonality(profile, sentimentScore);

          _personalityProfile = evolved;
          saveProfile(evolved);

          if (Math.abs(delta.newHealth - delta.previousHealth) > 0.001) {
            const updatedModifiers = getAvatarModifiers(evolved);
            _applyPersonalityModifiers(updatedModifiers);
          }

          // Only log debug info when running on localhost
          if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
            console.debug(
              `[Personality] ${delta.direction} | ` +
              `health ${delta.previousHealth.toFixed(2)} → ${delta.newHealth.toFixed(2)} | ` +
              `sentiment: ${sentimentScore.toFixed(3)}`,
            );
          }
        } catch (e) {
          console.warn('[Personality] Evolution error (non-fatal):', e);
        }

        // [FIX] Forward PIL metadata (chips, emotion, style) to ProactiveHub via event bus.
        // Listener in main.ts routes this to proactiveHub.onChatResponse(pilMeta).
        if (evt.pil) {
          document.dispatchEvent(new CustomEvent('akili:pil-response', { detail: evt.pil, bubbles: true }));
        }

      } else if (evt.type === 'error') {
        removeTypingIndicator();
        state.toolCallStatus = null;
        setToolCallStatus(null);
        setWaveformContext(null);
        appendMessage('bot', '\u26A0\uFE0F ' + (evt.text || 'Something went wrong. Please try again.'));
      }
    };

    removeTypingIndicator();
    if (mode === 'draft') {
      await draftStream(bodyObj, onEvent);
    } else if (mode === 'prep') {
      await prepStream(bodyObj, onEvent);
    } else {
      await chatStream(bodyObj, onEvent);
    }

    // Client-side empathy detection fallback
    if (botBubble && botText && !backendEmpathy && state.empathyDetect) {
      const { empathy, rest } = splitEmpathyLine(botText);
      if (empathy) {
        _updateBubbleEmpathy(botBubble, empathy);
        const textContainer = botBubble.querySelector('.ai-msg-text') as HTMLElement | null;
        if (textContainer && rest) {
          textContainer.innerHTML = renderMarkdown(rest);
        }
      }
    }

  } catch (e) {
    removeTypingIndicator();
    state.toolCallStatus = null;
    setToolCallStatus(null);
    setWaveformContext(null);
    appendMessage('bot', 'Sorry, I encountered an error. Please try again.');
    showToast('Connection error. Please try again.', 'error');
    console.error('[AkiliBrain]', e);
  } finally {
    state.streaming = false;
    const sb   = dom.sendBtn;
    const fbEl = dom.fab;
    if (sb)   { sb.classList.remove('spin'); sb.disabled = false; }
    if (fbEl) fbEl.classList.remove('streaming');
    updateSendState();
  }
}

// ── [PERSONALITY] Public accessor for personality profile ─────────────────────

export function getActivePersonalityProfile(): PersonalityProfile | null {
  return _personalityProfile;
}

export function refreshPersonalityProfile(): void {
  const userId = resolveUserId();
  _personalityProfile = loadOrCreateProfile(userId);
  _initAvatarPersonality(_personalityProfile);
}
